/**
 * Carrier Access — the carriers reaching a port, derived by the backend from
 * the related PortAllocation's description (`LM<CARRIER>` for last mile,
 * `CARR_<CARRIER>` for a backbone or trunk link).
 *
 * Shared by the ports list and the port detail card so both surfaces label and
 * order carriers identically. The backend already resolves the display `name`
 * (curated vendor, then the known-code map, then the raw code), so the UI
 * never maps codes itself.
 */

/** Marker the backend read the carrier from. */
export type CarrierAccessSource = "lm" | "carr";

export interface CarrierAccess {
    /** Carrier mnemonic as networking wrote it, e.g. `IENTC`. */
    carrierCode: string;
    /** Resolved display name; falls back to `carrierCode`. */
    name: string;
    source: CarrierAccessSource;
}
// The backend also stores the circuit and service request for each carrier but
// does not send them, since nothing displays them yet. See
// `transformCarrierAccess` in the backend's ports `common-helpers.js`.

/**
 * Read the `carrierAccess` array off a raw port payload.
 *
 * Ports served by a backend that predates the feature have no such field, so
 * an absent or malformed value is normalized to an empty array — callers can
 * then treat "no carrier" and "field not deployed yet" the same way.
 */
export const shapeCarrierAccess = (raw: unknown): CarrierAccess[] => {
    const entries = (raw as { carrierAccess?: unknown } | null | undefined)
        ?.carrierAccess;
    if (!Array.isArray(entries)) return [];

    return entries
        .filter(
            (entry): entry is CarrierAccess =>
                typeof entry?.carrierCode === "string" &&
                entry.carrierCode.trim() !== ""
        )
        .map((entry) => ({
            carrierCode: entry.carrierCode,
            name: entry.name?.trim() || entry.carrierCode,
            source: entry.source,
        }));
};

/**
 * Comma-separated carrier names for a read-only cell or detail row.
 *
 * Last mile is listed first: it is the carrier the customer's own access runs
 * over, so it is the one they came to the page for. Names are de-duplicated
 * because one carrier can be named by two different markers on the same port
 * (IENTC reaches port `10-C09582` as both its last mile and its trunk carrier),
 * and "IENTC, IENTC" would read as a bug.
 *
 * Returns an empty string when the port names no carrier, letting each surface
 * pick its own placeholder.
 */
export const formatCarrierAccessNames = (
    carriers: CarrierAccess[]
): string => {
    if (carriers.length === 0) return "";

    const lastMileFirst = [...carriers].sort((a, b) => {
        if (a.source === b.source) return a.name.localeCompare(b.name);
        return a.source === "lm" ? -1 : 1;
    });

    return [...new Set(lastMileFirst.map((carrier) => carrier.name))].join(
        ", "
    );
};
